import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_BASE_URL;

const CategoryGalleries = () => {
  const { categorySlug } = useParams();
  const [category, setCategory] = useState(null);
  const [galleries, setGalleries] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [status, setStatus] = useState("loading");

  const load = useCallback(async (nextCursor = null) => {
    setStatus(nextCursor ? "loading-more" : "loading");
    try {
      const response = await axios.get(`${API_URL}/api/collections/${categorySlug}/galleries`, {
        params: { limit: 12, ...(nextCursor ? { cursor: nextCursor } : {}) },
      });
      setCategory(response.data.category);
      setGalleries((current) => nextCursor ? [...current, ...response.data.galleries] : response.data.galleries);
      setCursor(response.data.nextCursor);
      setHasMore(Boolean(response.data.hasMore));
      setStatus("ready");
    } catch (error) {
      console.error("Unable to load category:", error.response?.data || error.message);
      setStatus("error");
    }
  }, [categorySlug]);

  useEffect(() => {
    setGalleries([]);
    load();
  }, [load]);

  if (status === "loading") {
    return <div className="flex min-h-screen items-center justify-center bg-[#111] text-white">Loading gallery…</div>;
  }

  if (status === "error") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#111] text-white">
        <p>This collection could not be loaded.</p>
        <button onClick={() => load()} className="border border-white/40 px-5 py-2">Try again</button>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-[#111] px-5 pb-20 pt-32 text-white sm:px-10 lg:px-16">
      <Link to="/galleries" className="text-xs uppercase tracking-[0.25em] text-white/60 hover:text-white">
        ← All collections
      </Link>
      <header className="mb-12 mt-10 max-w-3xl">
        <h1 className="text-5xl font-light tracking-tight sm:text-7xl">{category?.name}</h1>
        <p className="mt-5 max-w-xl leading-relaxed text-white/65">{category?.description}</p>
      </header>
      {galleries.length ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {galleries.map((gallery) => (
            <Link
              key={gallery.id}
              to={gallery.slug ? `/galleries/${gallery.slug}` : `/gallery/${gallery.id}`}
              className="group relative aspect-[4/3] overflow-hidden bg-white/5"
            >
              {gallery.cover_url ? (
                <img src={gallery.cover_url} alt="" loading="lazy" className="h-full w-full object-cover transition duration-700 group-hover:scale-105" />
              ) : (
                <div className="flex h-full items-center justify-center text-white/50">Cover coming soon</div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
              <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-5">
                <h2 className="text-xl">{gallery.title}</h2>
                {gallery.has_password && <span className="text-xs uppercase tracking-widest text-white/70">Private</span>}
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <p className="text-white/60">New work will be added here soon.</p>
      )}
      {hasMore && (
        <div className="mt-10 text-center">
          <button
            onClick={() => load(cursor)}
            disabled={status === "loading-more"}
            className="border border-white/35 px-6 py-3 text-sm uppercase tracking-[0.2em] disabled:opacity-50"
          >
            {status === "loading-more" ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </main>
  );
};

export default CategoryGalleries;
